import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/apiClient";

const OnboardingContext = createContext(null);

const EMPTY = { plan: {}, tier: null, debtor: {}, brokers: {}, billing: {} };

export const OnboardingProvider = ({ children }) => {
  const [data, setData] = useState(EMPTY);
  const [step, setStep] = useState("plan");
  const [loaded, setLoaded] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        const { data: state } = await apiClient.get("/onboarding/state");
        if (cancelled) return;
        setCompleted(!!state.completed);
        if (!state.completed) {
          setData({ ...EMPTY, ...(state.data || {}) });
          setStep(state.step || "plan");
        }
      } catch {
        // ignore — apiClient interceptor already handles 401 refresh/logout
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  const setSection = useCallback((section, value) => {
    setData((prev) => ({ ...prev, [section]: value }));
  }, []);

  const patchSection = useCallback((section, partial) => {
    setData((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), ...partial } }));
  }, []);

  const savePartial = useCallback(
    async (nextStep, overrideData) => {
      const payloadData = overrideData || data;
      try {
        await apiClient.post("/onboarding/save-partial", {
          step: nextStep,
          data: payloadData,
        });
      } catch {
        // swallow — user can retry on Continue
      }
    },
    [data]
  );

  const value = useMemo(
    () => ({
      loaded,
      completed,
      step,
      setStep,
      data,
      setData,
      setSection,
      patchSection,
      savePartial,
    }),
    [loaded, completed, step, data, setSection, patchSection, savePartial]
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error("useOnboarding must be used inside <OnboardingProvider>");
  return ctx;
};
